import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AboutModal = ({ isOpen, onClose }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isDarkMode: darkMode } = useDarkMode();

  useEffect(() => {
    if (isOpen) {
      fetchAboutContent();
    }
  }, [isOpen]);

  const fetchAboutContent = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API}/about`);
      setContent(response.data.content || '');
    } catch (err) {
      console.error('Error fetching about content:', err);
      setError('Error loading about content');
      // Set fallback content
      setContent(`## 📩 Contacto

¿Tienes preguntas, sugerencias o deseas colaborar con esta misión?

Puedes escribirnos al siguiente correo:

**DivinaVoluntadApp@gmail.com**`);
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (markdown) => {
    if (!markdown) return '';

    let html = markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mb-2 mt-4">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-bold mb-3 mt-6">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mb-4 mt-8">$1</h1>')
      
      // Bold and italic
      .replace(/\*\*(.*)\*\*/gim, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*)\*/gim, '<em class="italic">$1</em>')
      
      // Links - handle both [text](url) and [text] (url) formats
      .replace(/\[([^\]]+)\]\s*\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-purple-600 hover:text-purple-800 underline">$1</a>')
      
      // Images
      .replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img src="$2" alt="$1" class="max-w-full h-auto rounded-lg shadow-md my-4" />')
      
      // Code blocks (simple)
      .replace(/```([^`]+)```/gim, '<pre class="bg-gray-100 p-3 rounded-lg overflow-x-auto my-3"><code>$1</code></pre>')
      .replace(/`([^`]+)`/gim, '<code class="bg-gray-100 px-2 py-1 rounded text-sm">$1</code>')
      
      // Lists
      .replace(/^\* (.+$)/gim, '<li class="ml-4">• $1</li>')
      .replace(/^- (.+$)/gim, '<li class="ml-4">• $1</li>')
      .replace(/^\d+\. (.+$)/gim, '<li class="ml-4">$1</li>')
      
      // Line breaks
      .replace(/\n\n/gim, '</p><p class="mb-4">')
      .replace(/\n/gim, '<br />');

    // Wrap in paragraph tags
    html = '<p class="mb-4">' + html + '</p>';

    // Clean up empty paragraphs
    html = html.replace(/<p class="mb-4"><\/p>/gim, '');

    return html;
  };

  // Handle clicks outside modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className={`relative w-full max-w-2xl max-h-[90vh] rounded-lg shadow-xl ${
          darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className="text-2xl font-bold">Acerca de</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full transition-colors duration-200 ${
              darkMode 
                ? 'hover:bg-gray-700 text-gray-300 hover:text-white' 
                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
            }`}
            aria-label="Cerrar modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
                darkMode ? 'border-purple-400' : 'border-purple-600'
              }`}></div>
              <span className="ml-3">Cargando...</span>
            </div>
          ) : error ? (
            <div className={`text-center py-8 ${
              darkMode ? 'text-red-400' : 'text-red-600'
            }`}>
              <p>{error}</p>
              <button
                onClick={fetchAboutContent}
                className={`mt-4 px-4 py-2 rounded-lg transition-colors duration-200 ${
                  darkMode
                    ? 'bg-purple-700 hover:bg-purple-600 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                Reintentar
              </button>
            </div>
          ) : (
            <div 
              className={`prose max-w-none ${
                darkMode ? 'prose-invert' : ''
              }`}
              dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
              style={{
                lineHeight: '1.6',
                color: darkMode ? '#e5e7eb' : '#374151'
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AboutModal;